import { z } from "zod";
/**
 * 設問定義のzodスキーマ（唯一の定義元）。
 * saveDraft（サーバ）とエディタ（クライアント）で共有する。— 仕様書 §5, §6
 *
 * 「編集できるのは定義データ。エンジンが解釈できる語彙の範囲内」— 仕様書 §1.1
 * この語彙をここで固定する。
 */
export const TagIdSchema = z.enum([
    "T1", "T2", "T3", "T4", "T5", "T6", "T7", "T8", "T9", "T10",
]);
/** 重みは 0.5 / 1 / 2 のみ（管理画面のプルダウンと一致）— 仕様書 §4.2 */
export const WeightSchema = z.union([z.literal(0.5), z.literal(1), z.literal(2)]);
export const TagWeightsSchema = z.record(TagIdSchema, WeightSchema);
export const OptionSchema = z.object({
    id: z.string().min(1),
    label: z.string().min(1),
    tags: TagWeightsSchema.default({}),
    flags: z.array(z.string()).default([]),
    /** Q5用: この選択肢が展開する設問ブロック */
    block: z.string().optional(),
    /** 結果文の文脈切替など、非スコアの補助情報 */
    meta: z.record(z.string(), z.string()).optional(),
});
/** 表示条件: 先行設問IDごとに「いずれかを選んでいれば表示」 */
export const ShowIfSchema = z.record(z.string(), z.array(z.string()));
/** 明示分岐（goto）。showIfで表現できない構造用の予備語彙 */
export const NextRuleSchema = z.union([
    z.object({ when: z.object({ in: z.array(z.string()) }), goto: z.string() }),
    z.object({ default: z.string() }),
]);
export const QuestionSchema = z.object({
    id: z.string().min(1),
    block: z.string().min(1),
    text: z.string().min(1),
    type: z.enum(["single", "multi"]),
    options: z.array(OptionSchema).min(1),
    showIf: ShowIfSchema.optional(),
    next: z.array(NextRuleSchema).optional(),
    minSelect: z.number().int().nonnegative().optional(),
    maxSelect: z.number().int().positive().optional(),
    disabled: z.boolean().optional(),
});
export const BlockSchema = z.object({
    label: z.string().min(1),
    entry: z.string().min(1),
    questionIds: z.array(z.string()),
});
export const AreaOptionSchema = z.object({
    id: z.string().min(1),
    label: z.string().min(1),
    block: z.string().min(1),
});
export const AreaQuestionSchema = z.object({
    id: z.string().min(1),
    text: z.string().min(1),
    help: z.string().optional(),
    type: z.literal("multi"),
    minSelect: z.number().int().positive().default(1),
    maxSelect: z.number().int().positive().default(3),
    options: z.array(AreaOptionSchema).min(1),
});
export const ResultTypeSchema = z.object({
    id: z.string().min(1),
    name: z.string().min(1),
    topTagIn: z.array(TagIdSchema).min(1),
    summary: z.string().min(1),
});
export const ResultTemplateSchema = z.object({
    headline: z.string().min(1),
    body: z.string().min(1),
    change: z.string().optional(),
});
export const RoutingSchema = z.object({
    byEmployees: z.record(z.string(), z.object({ primary: z.string(), note: z.string() })),
    staffAddon: z.string().optional(),
});
export const UiTextsSchema = z.record(z.string(), z.string());
export const DefinitionSchema = z.object({
    version: z.string().optional(),
    startQuestion: z.string().min(1),
    fixedOrder: z.array(z.string()).min(1),
    tags: z.record(TagIdSchema, z.object({ label: z.string(), solution: z.string().optional() })),
    blocks: z.record(z.string(), BlockSchema),
    areaQuestion: AreaQuestionSchema,
    industryMap: z.record(z.string(), z.string()),
    conditionalOrder: z.array(z.string()),
    questions: z.array(QuestionSchema).min(1),
    resultTypes: z.array(ResultTypeSchema).min(1),
    tieBreakOrder: z.array(TagIdSchema),
    resultTemplates: z.record(TagIdSchema, ResultTemplateSchema),
    routing: RoutingSchema,
    uiTexts: UiTextsSchema,
});
/** questionSets/{setId} ドキュメント（definition を包む台帳）— 仕様書 §2.2 */
export const QuestionSetSchema = z.object({
    label: z.string(),
    status: z.enum(["draft", "published", "archived"]),
    createdAt: z.any().optional(),
    createdBy: z.string().optional(),
    publishedAt: z.any().optional(),
    basedOn: z.string().optional(),
    definition: DefinitionSchema,
});
export function parseDefinition(input) {
    return DefinitionSchema.parse(input);
}
//# sourceMappingURL=schema.js.map